type SocialProvider = "google";
type SocialOAuthMode = "login" | "register" | "link";

type PendingAuthContext = {
  provider: SocialProvider;
  mode: SocialOAuthMode;
  state: string;
  nonce: string;
};

type SocialCallbackSuccess = {
  provider: SocialProvider;
  mode: SocialOAuthMode;
  idToken: string;
};

type SocialCallbackFailure = {
  error: string;
};

const STORAGE_KEY = "cg.social.oauth.ctx";

/** Путь редиректа после Google OAuth (implicit id_token). Должен совпадать с redirect URI в консоли Google. */
export const SOCIAL_OAUTH_CALLBACK_PATH = "/auth/callback";

function readEnv() {
  return (import.meta as {
    env?: {
      VITE_GOOGLE_CLIENT_ID?: string;
      VITE_GOOGLE_REDIRECT_URI?: string;
    };
  }).env;
}

function randomToken(prefix: string): string {
  const uuid = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}-${Math.random().toString(36).slice(2, 14)}`;
  return `${prefix}_${uuid.replaceAll("-", "")}`;
}

function getRedirectUri(provider: SocialProvider): string {
  const env = readEnv();
  const raw = provider === "google" ? env?.VITE_GOOGLE_REDIRECT_URI : undefined;
  const fallback = `${window.location.origin}${SOCIAL_OAUTH_CALLBACK_PATH}`;
  if (!raw?.trim()) return fallback;
  return raw.trim();
}

function getClientId(provider: SocialProvider): string {
  const env = readEnv();
  const value = provider === "google" ? env?.VITE_GOOGLE_CLIENT_ID : undefined;
  if (!value?.trim()) {
    throw new Error("Отсутствует VITE_GOOGLE_CLIENT_ID.");
  }
  return value.trim();
}

function saveContext(ctx: PendingAuthContext): void {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
}

function readContext(): PendingAuthContext | null {
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingAuthContext;
    if (!parsed?.provider || !parsed?.mode || !parsed.state || !parsed.nonce) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearContext(): void {
  window.sessionStorage.removeItem(STORAGE_KEY);
}

function parseCallbackParams(url: URL): URLSearchParams {
  const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  if (hash) return new URLSearchParams(hash);
  return url.searchParams;
}

export function startSocialRedirect(provider: SocialProvider): void {
  startSocialRedirectWithMode(provider, "login");
}

export function startSocialRedirectWithMode(provider: SocialProvider, mode: SocialOAuthMode): void {
  const state = randomToken(`state_${provider}`);
  const nonce = randomToken(`nonce_${provider}`);
  saveContext({ provider, mode, state, nonce });

  const redirectUri = getRedirectUri(provider);
  const clientId = getClientId(provider);
  const query = new URLSearchParams();

  query.set("client_id", clientId);
  query.set("redirect_uri", redirectUri);
  query.set("response_type", "id_token");
  query.set("scope", "openid email profile");
  query.set("prompt", "select_account");
  query.set("state", state);
  query.set("nonce", nonce);
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${query.toString()}`);
}

export function parseSocialCallback():
  | SocialCallbackSuccess
  | SocialCallbackFailure
  | null {
  const url = new URL(window.location.href);
  const isCallbackPath = url.pathname === SOCIAL_OAUTH_CALLBACK_PATH;
  if (!isCallbackPath) {
    return null;
  }

  const params = parseCallbackParams(url);
  const error = params.get("error") || params.get("error_description");
  if (error) {
    clearContext();
    return { error };
  }

  const context = readContext();
  const idToken = params.get("id_token");
  const state = params.get("state");
  if (!context || !idToken || !state || state !== context.state) {
    clearContext();
    return { error: "Ошибка проверки OAuth состояния. Попробуйте снова." };
  }

  clearContext();
  return {
    provider: context.provider,
    mode: context.mode,
    idToken,
  };
}

export function clearSocialCallbackUrl(): void {
  if (window.location.pathname !== SOCIAL_OAUTH_CALLBACK_PATH) return;
  window.history.replaceState({}, "", "/");
}
